import axios from "axios";

// ---------------------------------------------------------
// 🛑 GLOBAL STATE: MUST BE OUTSIDE THE FUNCTION! 🛑
// This ensures all components share the same lock and queue.
// ---------------------------------------------------------
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = []; // Clear the queue once processed
};

export default function Api() {
    const baseURL = "https://ugpapi.mgu.ac.in/ugp/api/";

    const http = axios.create({
        baseURL,
        headers: { 'Content-Type': 'application/json' },
    });

    // 1. REQUEST INTERCEPTOR: Attach the Access Token
    http.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // 2. RESPONSE INTERCEPTOR: Refresh Token Queueing
    http.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                
                // If a refresh is ALREADY happening, queue this request
                if (isRefreshing) {
                    return new Promise(function(resolve, reject) {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return http(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                // Lock it down so subsequent requests get queued
                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const refreshToken = localStorage.getItem('refresh_token');

                    if (!refreshToken) {
                        throw new Error("No refresh token available");
                    }

                    // Perform the actual refresh call
                    const response = await axios.post(`${baseURL}refresh-token`, {
                        refresh_token: refreshToken
                    });

                    if (response.data.status_code === 'MSG4') {
                        const newToken = response.data.token;
                        const newRefreshToken = response.data.refresh_token;
                        
                        localStorage.setItem('token', newToken);
                        localStorage.setItem('refresh_token', newRefreshToken);
                        
                        // Update the original failed request
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        
                        // Process the queue: resolve all waiting requests with the new token
                        processQueue(null, newToken);

                        // Retry the original request
                        return http(originalRequest);
                    }
                } catch (refreshError) {
                    console.error("Refresh token failed, logging out.", refreshError);
                    processQueue(refreshError, null);
                    
                    localStorage.clear();
                    window.location.href = '/';
                    return Promise.reject(refreshError);
                } finally {
                    // Always unlock the state when done
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );

    return {
        http,
    };
};